import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { ArcheIconImg, DefaultArcheIcon } from '@/lib/arche-icon-url';
import { ArcheHeader } from './ArcheHeader';

interface ArcheEngineLandingProps {
  /** The arche id, used for the header and to fetch the large icon. */
  archeId: string;
  /** Display title shown in the header and as the icon's accessible name. */
  title: string;
  /** Explanatory copy under the "Nothing to see here" heading. */
  description: ReactNode;
  /** Call-to-action buttons (e.g. links out to the games that use this engine). */
  actions: ReactNode;
  /** Optional trailing slot — e.g. an admin entry-point card for privileged users. */
  footer?: ReactNode;
}

/**
 * Shared landing screen for "engine" arches — arches that power games or plugins
 * rather than being user-facing apps themselves (DFE Mining, DFE Game State,
 * Crafting, …). Renders the large arche icon, a friendly "nothing to see here"
 * explainer, caller-supplied action buttons, and an optional footer slot.
 *
 * Keeping this in the arche framework layer (alongside ArcheLayout / ArcheHeader)
 * avoids triplicating the icon-fetch + layout across every engine arche and keeps
 * their landing pages down to just their own copy and links.
 */
export function ArcheEngineLanding({
  archeId,
  title,
  description,
  actions,
  footer,
}: ArcheEngineLandingProps) {
  // Fetch the arche record purely to render its uploaded icon at large size.
  // Non-critical: if it fails (or has no icon), we fall back to DefaultArcheIcon.
  const { data: arche } = useQuery({
    queryKey: queryKeys.marketplaceArche.detail(archeId),
    queryFn: () => api.getMarketplaceArche(archeId),
    staleTime: 5 * 60 * 1000,
  });

  const iconClassName = 'w-28 h-28 rounded-2xl shadow-sm object-cover';

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId={archeId} title={title} />
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-2xl mx-auto w-full flex flex-col items-center text-center gap-6 py-16">
          {arche?.imageRef ? (
            <ArcheIconImg
              imageRef={arche.imageRef}
              size={512}
              alt={title}
              className={iconClassName}
            />
          ) : (
            <DefaultArcheIcon className={iconClassName} role="img" aria-label={title} />
          )}

          <div className="space-y-3">
            <h2 className="text-2xl font-semibold">Nothing to see here</h2>
            <p className="text-muted-foreground leading-relaxed">{description}</p>
          </div>

          {actions}

          {footer}
        </div>
      </div>
    </div>
  );
}
